import { useRef, useState, useCallback, useEffect } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useForm } from "@/features/tutor/context/FormContext.tsx";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";

type CaptionsToken = {
  caption_text: string;
  is_math: boolean;
  flush: boolean;
  words: { text: string; start_ms: number; duration_ms: number }[];
};

/**
 * Detects the first time the tutor says the student's display name in captions.
 * Fires at most once per (sessionId, name, language, voiceId) combination per
 * component lifecycle — suppressed for the rest of the session once it fires.
 */
export const useNameMentionDetector = () => {
  const { user } = useAuthStore();
  const { sessionId } = useSessionStore();
  const { formData } = useForm();
  const { subscribe } = useRuntimeContext();

  const [showToast, setShowToast] = useState(false);
  // Tracks the key we last showed the toast for — suppresses repeats within session.
  const shownForKeyRef = useRef<string | null>(null);

  const dismiss = useCallback(() => setShowToast(false), []);

  useEffect(() => {
    // Match the backend's _raw_name logic: prefer displayUsername, fall back to first
    // token of name. Avoids false-negative when username is a handle (e.g. "mjohnson123").
    const name = user?.displayUsername || user?.name?.split(" ")[0] || "";
    if (!name) return;

    const language = formData.language.value;
    const voiceId = formData.voice.voiceId;
    const key = `${sessionId ?? ""}:${name.toLowerCase()}:${language}:${voiceId}`;

    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Word-boundary check: name as a whole word, case-insensitive.
    // \b is used instead of lookbehind (?<![\w]) for Safari < 16.4 compatibility
    // (lookbehind assertions throw SyntaxError on older Safari versions).
    const nameRe = new RegExp(`\\b${escaped}\\b`, "i");

    const unsubscribe = subscribe(
      "CAPTIONS_UPDATE",
      (chunk: { chunk_data: string }) => {
        // Already shown for this (session, name, language, voice) combo
        if (shownForKeyRef.current === key) return;

        let token: CaptionsToken;
        try {
          token = JSON.parse(chunk.chunk_data) as CaptionsToken;
        } catch {
          return;
        }

        // Skip math tokens — names won't appear in LaTeX
        if (token.is_math) return;

        const captionText = token.caption_text ?? "";
        if (nameRe.test(captionText)) {
          shownForKeyRef.current = key;
          setShowToast(true);
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [
    user,
    sessionId,
    formData.language.value,
    formData.voice.voiceId,
    subscribe,
  ]);

  return { showToast, dismiss };
};
